/**
 * "Did the player just heal, and by how much?"
 *
 * Janeks: *"show +XX on player when healing, even if auto regenerate."*
 *
 * ─── Why this is derived and not a new server event ─────────────────────────
 * There is no heal broadcast to listen to. `AttackResultBroadcast` carries no
 * heal channel, the Colyseus room has no message for using an item, and passive
 * regen is **lazy** by this project's own core rule — nothing ticks it, so there
 * is no moment at which a server could emit "you regenerated 3 HP". HP simply
 * reads higher the next time something asks.
 *
 * But `health:updated` is already pushed on every change and `HealthSheet.hp`
 * is already in `gameStateStore`. An increase between two of those is a heal,
 * whatever caused it — a kit, a consumable, or regen banked by the next read.
 * That covers "even if auto regenerate" with no new endpoint, no new socket
 * event and no polling.
 *
 * ─── What it deliberately does NOT report ───────────────────────────────────
 * - **Damage.** In a duel that comes from the room's `attackResult`, which
 *   knows who swung and whether it crit; a sheet delta knows none of that and
 *   would double up on every hit.
 * - **The first reading.** There is no "before" to compare against, so the
 *   initial sheet only establishes a baseline. Otherwise loading the game at
 *   full health would announce a heal for the player's entire HP pool.
 * - **A max-HP change.** Equipping a vest that grants +10 HP raises `hp` with
 *   `maxHP`; that is a bigger pool, not a heal, and flashing `+10` for it would
 *   be a lie. Ignored by watching `maxHP` alongside.
 * - **A respawn.** Death sets HP to a fraction of max, which reads as an
 *   enormous heal on the way back up from 0. Reported as a heal it would put a
 *   cheerful green `+7` over a player who has just lost their bag.
 */
import type { HealthSheet } from './game-state.types';

export interface HealDelta {
  /** HP restored. Always > 0 — a non-heal returns null instead. */
  amount: number;
}

export interface HealWatcherState {
  hp: number;
  maxHP: number;
  /** Death resets the pool; the climb out of it is not a heal. */
  downed: boolean;
  atMedCenter: boolean;
}

/**
 * Compares two readings and reports a heal, or null.
 *
 * Pure, and exported for exactly that reason — every interesting case here is a
 * thing that LOOKS like a heal and is not.
 */
export function healBetween(
  before: HealWatcherState | null,
  after: HealWatcherState,
): HealDelta | null {
  // No baseline yet — this reading establishes one.
  if (!before) return null;
  // A bigger pool is not a heal.
  if (after.maxHP !== before.maxHP) return null;
  // Waking up at the Med-Center is not a heal, it is the end of a death.
  if (before.downed && !after.downed) return null;
  if (!before.atMedCenter && after.atMedCenter) return null;

  const gained = after.hp - before.hp;
  if (gained <= 0) return null;
  return { amount: gained };
}

/** Narrows the store's discriminated union down to the fields a comparison needs. */
export function watcherStateOf(sheet: HealthSheet | null | undefined): HealWatcherState | null {
  if (!sheet || sheet.state !== 'ACTIVE') return null;
  return {
    hp: sheet.hp, maxHP: sheet.maxHP, downed: sheet.downed, atMedCenter: sheet.atMedCenter,
  };
}

/**
 * Stateful wrapper around {@link healBetween} — hand it each new sheet, it
 * answers with a heal or null and remembers the reading.
 *
 * One per view, created on mount: the baseline must reset when the view does,
 * or the first sheet after a scene change reads as a heal for the difference
 * accumulated while the view was gone.
 */
export function createHealWatcher(): (sheet: HealthSheet | null | undefined) => HealDelta | null {
  let previous: HealWatcherState | null = null;
  return (sheet) => {
    const next = watcherStateOf(sheet);
    // An UNCREATED or absent sheet clears the baseline rather than holding a
    // stale one across a character change.
    if (!next) { previous = null; return null; }
    const delta = healBetween(previous, next);
    previous = next;
    return delta;
  };
}
